import './RuntimeHeader.scss';

import React, {useState, useEffect} from 'react';
import PropTypes from 'prop-types';

import {Button} from 'react-bootstrap';
import FastProgressBar from 'components/FastProgressBar/FastProgressBar';
import {useDispatch} from 'react-redux';
import useHumidityWarnings from 'lib/hooks/useHumidityWarnings';
import {checkLoadCellError} from 'stores/business/loadCellErrorBusiness';
import HeaderLayout from '../../../components/Layout/HeaderLayout';
import {DrawerButtonTitle} from '../../Common';
import {RuntimeMode, RuntimeType, SignalREvent} from '../../../constants/dataEnum';
import {convertSecondToHoursMinuteSecond} from '../../../lib/fastCommonModule';

import withIntl from '../../../lib/withIntl';

function RuntimeHeader({
  title,
  progress,
  status,
  runtimeType,
  intl,
  onPause,
  onAbortWithPause,
  onResume,
  onAbort,
  onReport,
  onClose,
  instrumentHub,
  remainingTime,
  pauseRemainingTime,
  readOnly,
  setRuntimeRemainingTime,
  onReRun,
  userName,
  abortOnly,
  hideReportButton,
  hideRerunButton,
  showRemainingTime,
}) {
  const dispatch = useDispatch();
  const checkHumidityWarnings = useHumidityWarnings();
  let runtimeActionButton = '';
  let okButtonLabel = '';
  let btStyle = '';

  const handleOnClickReRun = async () => {
    const humidityOk = await checkHumidityWarnings();
    if (!humidityOk) return;

    const isLoadCellError = await dispatch(checkLoadCellError(null, onReRun));

    if (isLoadCellError) return;

    onReRun();
  };

  switch (status) {
    case RuntimeMode.RUNNING:
      runtimeActionButton = (
        <>
          {!abortOnly && (
            <Button onClick={onPause} id="pause" bsStyle="primary">
              {intl.formatMessage({id: 'runtimeHeader.button.pause'})}
            </Button>
          )}
          <Button onClick={onAbortWithPause} id="abort" bsStyle="danger">
            {intl.formatMessage({id: 'runtimeHeader.button.abort'})}
          </Button>
        </>
      );
      break;
    case RuntimeMode.PAUSING:
    case RuntimeMode.ABORTING:
      runtimeActionButton = (
        <>
          {!abortOnly && (
            <Button onClick={onPause} id="pause" bsStyle="primary" disabled>
              {status === RuntimeMode.PAUSING
                ? intl.formatMessage({id: 'runtimeHeader.button.pausing'})
                : intl.formatMessage({id: 'runtimeHeader.button.pause'})}
            </Button>
          )}
          <Button onClick={onAbortWithPause} id="abort" bsStyle="danger" disabled>
            {status === RuntimeMode.ABORTING
              ? intl.formatMessage({id: 'runtimeHeader.button.aborting'})
              : intl.formatMessage({id: 'runtimeHeader.button.abort'})}
          </Button>
        </>
      );
      break;
    case RuntimeMode.ERROR_ABORT_MOTOR_COLLISION_SKIP:
      runtimeActionButton = (
        <>
          {!abortOnly && (
            <Button onClick={onPause} id="pause" bsStyle="primary">
              {intl.formatMessage({id: 'runtimeHeader.button.pause'})}
            </Button>
          )}
          <Button onClick={onAbortWithPause} id="abort" bsStyle="danger">
            {intl.formatMessage({id: 'runtimeHeader.button.abort'})}
          </Button>
        </>
      );
      break;
    case RuntimeMode.PAUSED:
    case RuntimeMode.ERROR_ABORT:
    case RuntimeMode.ERROR_ABORT_MOTOR_SKIP:
    case RuntimeMode.ERROR_MISSING_TRAY:
      okButtonLabel = intl.formatMessage({id: 'runtimeHeader.button.resume'});
      btStyle = 'success';

      switch (status) {
        case RuntimeMode.ERROR_ABORT:
        case RuntimeMode.ERROR_MISSING_TRAY:
          okButtonLabel = intl.formatMessage({id: 'runtimeHeader.button.retry'});
          break;
        case RuntimeMode.ERROR_ABORT_MOTOR_SKIP:
          okButtonLabel = intl.formatMessage({id: 'runtimeHeader.button.recover'});
          break;
        case RuntimeMode.PAUSED:
        default:
          break;
      }

      runtimeActionButton = (
        <>
          {!abortOnly && (
            <Button
              onClick={onResume}
              disabled={status === RuntimeMode.ERROR_MISSING_TRAY}
              id="resume"
              bsStyle={btStyle}
            >
              {okButtonLabel}
            </Button>
          )}
          <Button onClick={() => onAbort(status === RuntimeMode.ERROR_MISSING_TRAY)} id="abort" bsStyle="danger">
            {intl.formatMessage({id: 'runtimeHeader.button.abort'})}
          </Button>
        </>
      );
      break;

    case RuntimeMode.FINISH:
    case RuntimeMode.ABORTED:
      runtimeActionButton = !abortOnly ? (
        <>
          {!hideReportButton && (
            <Button bsStyle="primary" style={{marginRight: '12px'}} onClick={onReport}>
              {intl.formatMessage({id: 'runtimeHeader.button.report'})}
            </Button>
          )}
          <Button bsStyle="danger" style={{marginRight: '12px'}} onClick={onClose}>
            {intl.formatMessage({id: 'runtimeHeader.button.close'})}
          </Button>
        </>
      ) : (
        // eslint-disable-next-line react/jsx-no-useless-fragment
        <></>
      );
      break;
    default:
      break;
  }

  // #region handling runtime remaining timer
  const [remainingSeconds, setRemainingSeconds] = useState(remainingTime);

  const getProgressBarColor = () => {
    const isShowingStaticProgressBar = runtimeType === RuntimeType.CONSOLIDATE_TIPS && status === RuntimeMode.RUNNING;

    switch (status) {
      case RuntimeMode.ABORTED:
      case RuntimeMode.ERROR_ABORT:
      case RuntimeMode.ERROR_MISSING_TRAY:
        return 'error';
      case RuntimeMode.PAUSED:
        return 'on-hold';
      default:
        return `on-going${isShowingStaticProgressBar ? ' static' : ''}`;
    }
  };

  useEffect(() => {
    if (!instrumentHub) return undefined;

    instrumentHub.on(SignalREvent.REMAINING_TIME_UPDATE, (res) => {
      setRemainingSeconds(res);
      setRuntimeRemainingTime(res);
    });
    return () => {
      instrumentHub.off(SignalREvent.REMAINING_TIME_UPDATE);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instrumentHub]);

  useEffect(() => {
    const timer = setInterval(
      (runtimeModeStatus) => {
        setRemainingSeconds((prevState) => {
          if (
            (runtimeModeStatus !== RuntimeMode.RUNNING &&
              runtimeType !== RuntimeType.CONSOLIDATE_TIPS &&
              runtimeModeStatus !== RuntimeMode.PAUSING) ||
            pauseRemainingTime
          ) {
            clearInterval(timer);
            return prevState;
          }
          if (prevState > 1) {
            return prevState - 1;
          }
          return 0;
        });
      },
      1000,
      status,
    );
    return () => {
      clearInterval(timer);
    };
  }, [status, pauseRemainingTime, runtimeType]);
  // #endregion handling runtime remaining timer

  return (
    <HeaderLayout>
      <HeaderLayout.Left>
        <DrawerButtonTitle title={title} userName={userName} />
      </HeaderLayout.Left>
      <HeaderLayout.Center>
        <>
          <div className="progress-bar-container">
            <FastProgressBar
              className={getProgressBarColor()}
              width="39vw"
              height="props.height"
              label={`${progress} %`}
              progress={progress}
              fontSize="14pt"
            />
          </div>
          {showRemainingTime && (
            <div className="remaining-timer">{convertSecondToHoursMinuteSecond(remainingSeconds, true)}</div>
          )}
        </>
      </HeaderLayout.Center>
      <HeaderLayout.Right>
        {(!readOnly || abortOnly) && (
          <>
            {!hideRerunButton && !abortOnly && (status === RuntimeMode.FINISH || status === RuntimeMode.ABORTED) && (
              <Button onClick={handleOnClickReRun} id="re-run" bsStyle="success">
                RE-RUN
              </Button>
            )}
            {runtimeActionButton}
          </>
        )}
      </HeaderLayout.Right>
    </HeaderLayout>
  );
}

RuntimeHeader.propTypes = {
  title: PropTypes.string,
  progress: PropTypes.number.isRequired,
  status: PropTypes.string.isRequired,
  runtimeType: PropTypes.string.isRequired,
  intl: PropTypes.any.isRequired,
  onPause: PropTypes.func.isRequired,
  onAbortWithPause: PropTypes.func.isRequired,
  onResume: PropTypes.func.isRequired,
  onAbort: PropTypes.func.isRequired,
  onReport: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  instrumentHub: PropTypes.any.isRequired,
  remainingTime: PropTypes.number.isRequired,
  pauseRemainingTime: PropTypes.bool.isRequired,
  readOnly: PropTypes.bool,
  setRuntimeRemainingTime: PropTypes.func.isRequired,
  onReRun: PropTypes.func.isRequired,
  userName: PropTypes.string,
  abortOnly: PropTypes.bool.isRequired,
  hideReportButton: PropTypes.bool.isRequired,
  hideRerunButton: PropTypes.bool.isRequired,
  showRemainingTime: PropTypes.bool,
};

RuntimeHeader.defaultProps = {
  title: 'New Protocol 1',
  readOnly: true,
  showRemainingTime: true,
  userName: '',
};

function areEqual(prevProps, nextProps) {
  return (
    prevProps.title === nextProps.title &&
    prevProps.selectedMenu === nextProps.selectedMenu &&
    prevProps.progress === nextProps.progress &&
    prevProps.status === nextProps.status &&
    prevProps.pauseRemainingTime === nextProps.pauseRemainingTime &&
    prevProps.readOnly === nextProps.readOnly &&
    prevProps.userName === nextProps.userName &&
    prevProps.showRemainingTime === nextProps.showRemainingTime
  );
}

export default React.memo(withIntl(RuntimeHeader), areEqual);
